import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { CourseDesignContext } from '../context/CourseDesignContext';
import { fetchAllUsers, getESTDateString } from '../utils/serverFetchAndAddUtilities';
import './GradingStatistics.css';
import { CourseIDContext } from '../context/CourseIDContext';

const GradingStatistics = () => {
  const { courseDesignState } = useContext(CourseDesignContext);
  const [TAs, setTAs] = useState([]);
  const [studentsByTA, setStudentsByTA] = useState({});
  const [allStudents, setAllStudents] = useState([]);
  const [grades, setGrades] = useState([]);
  const { courseID } = useContext(CourseIDContext);
  const integrateOralExamsEnabled = courseDesignState.some(
    (item) => item.dataType === 'integrateOralExams' && item.dataText === 'Yes'
  );

  const hasTAs = courseDesignState.some(
    (item) => item.dataType === 'areThereTAs' && item.dataText === 'Yes'
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const users = await fetchAllUsers(courseID);
        // Fetch all student grades for the course (not filtered) so we can include oral exam data
        const resp = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/studentGrades/${courseID}`);
        const fetchedGrades = Array.isArray(resp.data)
          ? resp.data
          : resp.data && typeof resp.data === 'object'
            ? Object.values(resp.data).flat()
            : [];

        setGrades(fetchedGrades);

        if (hasTAs) {
          const taList = users.filter((user) => user.status.toLowerCase() === 'ta');
          setTAs(taList);

          const groupedStudents = {};
          taList.forEach((ta) => {
            groupedStudents[ta.username] = users.filter(
              (student) => student.TA === ta.username && student.status.toLowerCase() === 'student'
            );
          });
          setStudentsByTA(groupedStudents);
        } else {
          const studentList = users.filter((user) => user.status.toLowerCase() === 'student');
          setAllStudents(studentList);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [hasTAs, courseID]);

  const evaluations = courseDesignState.filter(
    (item) => item.dataType === 'evaluation'
  );

  // pick a selected evaluation (default to first if present)
  const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);

  // keep selection valid if evaluations change
  useEffect(() => {
    if (!evaluations || evaluations.length === 0) {
      setSelectedEvaluationId(null);
      return;
    }

    // Compute default: most recent past due, else earliest upcoming
    const sortedByDate = [...evaluations].sort((a, b) => a.questionCourseDate.localeCompare(b.questionCourseDate));
    const todayStr = getESTDateString(Date.now());
    const pastDue = sortedByDate.filter(e => e.questionCourseDate <= todayStr);
    const defaultEval = pastDue.length > 0 ? pastDue[pastDue.length - 1] : sortedByDate[0];

    // If current selection is invalid or not set, choose default as above
    const stillExists = evaluations.some(e => e.dataID === selectedEvaluationId);
    if (!stillExists || !selectedEvaluationId) {
      setSelectedEvaluationId(defaultEval?.dataID || null);
    }
  }, [evaluations, selectedEvaluationId]);

  const calculateStats = (evaluationID, students) => {
    const completedGrades = grades.filter(
      (grade) =>
        grade.evaluationDataID === evaluationID &&
        grade.gradingType === 'gradingComplete' &&
        grade.individualGradingData === 'yes' &&
        students.some((student) => student.username === grade.username)
    );

    const completedStudentUsernames = completedGrades.map(
      (grade) => grade.username
    );

    // Build a lookup of oral exam adjustments by username (if present)
    const oralAdjustmentByUser = grades
      .filter(
        (g) =>
          g.evaluationDataID === evaluationID &&
          g.gradingType === 'oralExamGrade'
      )
      .reduce((acc, g) => {
        const val = parseFloat(g.individualGradingData);
        acc[g.username] = isNaN(val) ? 0 : val;
        return acc;
      }, {});

    // Adjust written exam grades by subtracting oral adjustment when present
    const examGrades = grades
      .filter(
        (grade) =>
          grade.evaluationDataID === evaluationID &&
          grade.gradingType === 'examGrade' &&
          completedStudentUsernames.includes(grade.username)
      )
      .map((grade) => {
        const base = parseFloat(grade.individualGradingData);
        const oralAdj = oralAdjustmentByUser[grade.username] || 0;
        return base - oralAdj;
      })
      .filter((grade) => !isNaN(grade) && grade >= 60);

    const mean =
      examGrades.length > 0
        ? (examGrades.reduce((sum, grade) => sum + grade, 0) / examGrades.length).toFixed(2)
        : '--';

    const median =
      examGrades.length > 0
        ? (() => {
            const sorted = [...examGrades].sort((a, b) => a - b);
            const mid = Math.floor(sorted.length / 2);
            return sorted.length % 2 !== 0
              ? sorted[mid]
              : ((sorted[mid - 1] + sorted[mid]) / 2).toFixed(2);
          })()
        : '--';

    // Oral exam stats (only those marked oralExamComplete === 'yes')
    const oralCompleted = grades.filter(
      (grade) =>
        grade.evaluationDataID === evaluationID &&
        grade.gradingType === 'oralExamComplete' &&
        grade.individualGradingData === 'yes' &&
        students.some((student) => student.username === grade.username)
    );
    const oralCompletedUsernames = oralCompleted.map((g) => g.username);
    const oralGrades = grades
      .filter(
        (grade) =>
          grade.evaluationDataID === evaluationID &&
          grade.gradingType === 'oralExamGrade' &&
          oralCompletedUsernames.includes(grade.username)
      )
      .map((grade) => parseFloat(grade.individualGradingData))
      .filter((v) => !isNaN(v));

    const oralMean =
      oralGrades.length > 0
        ? (oralGrades.reduce((sum, v) => sum + v, 0) / oralGrades.length).toFixed(2)
        : '--';

    // Overall stats (only where both written and oral are complete)
    const bothCompletedUsernames = completedStudentUsernames.filter((u) =>
      oralCompletedUsernames.includes(u)
    );
    const overallGrades = grades
      .filter(
        (grade) =>
          grade.evaluationDataID === evaluationID &&
          grade.gradingType === 'examGrade' &&
          bothCompletedUsernames.includes(grade.username)
      )
      .map((g) => parseFloat(g.individualGradingData))
      .filter((v) => !isNaN(v) && v >= 60);

    const overallMean =
      overallGrades.length > 0
        ? (overallGrades.reduce((sum, v) => sum + v, 0) / overallGrades.length).toFixed(2)
        : '--';

    const overallMedian =
      overallGrades.length > 0
        ? (() => {
            const sorted = [...overallGrades].sort((a, b) => a - b);
            const mid = Math.floor(sorted.length / 2);
            return sorted.length % 2 !== 0
              ? sorted[mid]
              : ((sorted[mid - 1] + sorted[mid]) / 2).toFixed(2);
          })()
        : '--';

    return {
      progress: completedGrades.length,
      mean,
      median,
      oralProgress: oralCompleted.length,
      oralMean,
      overallMean,
      overallMedian,
    };
  };

  const selectedEvaluation = evaluations.find(e => e.dataID === selectedEvaluationId);

  return (
    <div className="grading-statistics">
      <div className="evaluation-selector">
        {evaluations.map((evaluation) => (
          <button
            key={evaluation.dataID}
            className={`evaluation-select-button ${selectedEvaluationId === evaluation.dataID ? 'selected' : ''}`}
            onClick={() => setSelectedEvaluationId(evaluation.dataID)}
          >
            {evaluation.dataText}
          </button>
        ))}
      </div>
      <table>
        <thead>
          <tr>
            {hasTAs && <th>TA Name</th>}
            {selectedEvaluation && (
              <React.Fragment key={selectedEvaluation.dataID}>
                <th>{integrateOralExamsEnabled ? 'Written work grading progress' : 'Grading Progress'}</th>
                <th>{integrateOralExamsEnabled ? 'Written work mean (excl. < 60)' : 'Mean (excl. < 60)'}</th>
                <th>{integrateOralExamsEnabled ? 'Written work median' : 'Median'}</th>
                {integrateOralExamsEnabled && (
                  <>
                    <th>Oral exam progress</th>
                    <th>Oral exam mean</th>
                    <th>Overall mean (excl. &lt; 60)</th>
                    <th>Overall median</th>
                  </>
                )}
              </React.Fragment>
            )}
          </tr>
        </thead>
        <tbody>
          {hasTAs
            ? TAs.map((ta) => (
                <tr key={ta.username}>
                  <td>{ta.fullname}</td>
                  {selectedEvaluation && (() => {
                    const { progress, mean, median, oralProgress, oralMean, overallMean, overallMedian } = calculateStats(
                      selectedEvaluation.dataID,
                      studentsByTA[ta.username] || []
                    );
                    return (
                      <React.Fragment key={selectedEvaluation.dataID}>
                        <td>
                          {progress}/{studentsByTA[ta.username]?.length || 0}
                        </td>
                        <td>{mean}</td>
                        <td>{median}</td>
                        {integrateOralExamsEnabled && (
                          <>
                            <td>{oralProgress}/{studentsByTA[ta.username]?.length || 0}</td>
                            <td>{oralMean}</td>
                            <td>{overallMean}</td>
                            <td>{overallMedian}</td>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })()}
                </tr>
              ))
            : allStudents.length > 0 && selectedEvaluation && (
                <tr>
                  {(() => {
                    const { progress, mean, median, oralProgress, oralMean, overallMean, overallMedian } = calculateStats(
                      selectedEvaluation.dataID,
                      allStudents
                    );
                    return (
                      <React.Fragment key={selectedEvaluation.dataID}>
                        <td>
                          {progress}/{allStudents.length}
                        </td>
                        <td>{mean}</td>
                        <td>{median}</td>
                        {integrateOralExamsEnabled && (
                          <>
                            <td>{oralProgress}/{allStudents.length}</td>
                            <td>{oralMean}</td>
                            <td>{overallMean}</td>
                            <td>{overallMedian}</td>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })()}
                </tr>
              )}
        </tbody>
      </table>
    </div>
  );
};

export default GradingStatistics;
